import React, { useEffect, useMemo, useState } from 'react';
// import { API } from 'aws-amplify';
import { AlertBlock } from '@chiroup/components/AlertBlock';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Input } from '@chiroup/components/form/Input';
import { TrivialTooltip } from '@chiroup/components/TrivialTooltip';
import { formatPhone } from '@chiroup/core/functions/format';
import { ListClinic } from '@chiroup/core/types/Clinic.type';
import {
  DevtoolsGetBillingPreview,
  DevtoolsGetPatientResponse,
  SearchFormInputs,
} from '@chiroup/core/types/Devtools.type';
import { Patient } from '@chiroup/core/types/Patient.type';
import { CheckCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import { SubmitHandler, useForm } from 'react-hook-form';
import useDevtools from '../../../../hooks/useDevtools';
import { generatePatientSubtitle } from '../../../common/helpers/generatePatientSubtitle';
import UploadedImage from '../../../common/uploader/UploadedImage';

type Props = {
  clinic: Partial<ListClinic>;
};

const Patients: React.FC<Props> = ({ clinic }) => {
  const { isFetching, getPatient, getPreview, purgePreview, postPurgeActions } =
    useDevtools('patient-purge');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SearchFormInputs>();
  const [searchResults, setSearchResults] = useState<DevtoolsGetPatientResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<SearchFormInputs>({
    fname: '',
    lname: '',
    clinicId: clinic?.ID ?? 0,
  });
  const [selectedPatients, setSelectedPatients] = useState<{
    [key: string]: Patient;
  }>({});

  const [searchResponsePatients, setSearchResponsePatients] = useState<{
    [key: string]: Patient;
  }>({});

  const [previewRows, setPreviewRows] = useState<DevtoolsGetBillingPreview[]>([]);

  const [actionRows, setActionRows] = useState<DevtoolsGetBillingPreview[]>([]);

  const rows = useMemo(() => {
    return actionRows && actionRows.length > 0
      ? []
      : previewRows && previewRows.length > 0
        ? []
        : (Object.values(selectedPatients) ?? []);
  }, [actionRows, previewRows, selectedPatients]);

  useEffect(() => {
    const resp = (searchResults?.items?.reduce((acc: any, item) => {
      acc[item.ID] = item;

      return acc;
    }, {}) ?? {}) as {
      [key: string]: Patient;
    };
    setSearchResponsePatients(resp);
  }, [searchResults]);

  const resultsArray = useMemo(() => {
    return previewRows && previewRows.length > 0
      ? []
      : (Object.values(searchResponsePatients) ?? []);
  }, [previewRows, searchResponsePatients]);

  const [confirmedPurgeActions, setConfirmedPurgeActions] = useState(false);
  const [confirmPurgeAction, setConfirmPurgeAction] = useState('');
  const [isPurging, setIsPurging] = useState(false);

  const cancelPurgeActions = () => {
    setActionRows([]);
    setPreviewRows([]);
    setPurgePatientTableDataList({});
    setSelectedPatients(() => ({}));
    setSearchInput(p => ({ ...p, fname: '', lname: '' }));
    setSearchResults(null);
    setConfirmPurgeAction('');
    setConfirmedPurgeActions(false);
    setPurgeResults(null);
  };

  const confirmPurgeActions = () => {
    setConfirmedPurgeActions(true);
  };

  const [purgeResults, setPurgeResults] = useState<{
    okays: any[];
    errors: any[];
    remainingRecords: DevtoolsGetBillingPreview[];
  } | null>(null);

  const finallyPurgeActions = async () => {
    if (confirmPurgeAction !== 'confirm') {
      setError('Please type "confirm" to proceed.');

      return;
    }

    setError(null);
    setIsPurging(true);
    const resp = await postPurgeActions?.({
      clinicId: clinic?.ID ?? 0,
      actions: actionRows,
    });
    setPurgeResults(resp);
    setConfirmedPurgeActions(false);
    setIsPurging(false);
  };

  const onSubmit: SubmitHandler<SearchFormInputs> = async _data => {
    setLoading(true);
    setError(null);

    try {
      const response = await getPatient?.(searchInput);
      setSearchResults(response ?? { items: [] });
    } catch (err: any) {
      setError(err.message || 'An error occurred while searching for patients.');
    } finally {
      setLoading(false);
    }
  };

  const previewBilling = async () => {
    try {
      const response = await getPreview?.({
        clinicId: clinic?.ID ?? 0,
        ids: Object.keys(selectedPatients),
      });
      setPreviewRows(response?.items ?? []);
    } catch (err: any) {
      setError(err.message || 'An error occurred while searching for patients.');
    } finally {
      setLoading(false);
    }
  };

  const purgeRecordsPreview = async () => {
    try {
      const response = await purgePreview?.({
        clinicId: clinic?.ID ?? 0,
        list: Object.entries(purgePatientTableDataList)
          .filter(([, v]) => v)
          .map(([k]) => k),
      });
      setPreviewRows(() => []);
      setActionRows(response?.items ?? []);
      setPurgePatientTableDataList({});
      setSelectedPatients(() => ({}));
      setSearchResults(null);
      setSearchInput(p => ({ ...p, fname: '', lname: '' }));
    } catch (err: any) {
      setError(err.message || 'An error occurred while searching for patients.');
    } finally {
      setLoading(false);
    }
  };

  const [purgePatientTableDataList, setPurgePatientTableDataList] = useState<{
    [key: string]: boolean;
  }>({});

  // return null; // Disabled.
  return (
    <div className="bg-white p-6 flex flex-col space-y-4">
      <h1 className="text-xl font-bold">Search Disabled Patients</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-row space-x-4 w-full">
          <div className="flex flex-col w-full">
            <label htmlFor="fname">Patient first name</label>
            <input
              id="fname"
              {...register('fname', {
                required: 'Patient first name is required',
              })}
              value={searchInput.fname}
              onChange={e => {
                setSearchInput({
                  ...searchInput,
                  fname: e.target.value,
                });
              }}
              className="rounded-md text-gray-500"
            />
            {errors.fname && <p>{errors?.fname?.message}</p>}
          </div>
          <div className="flex flex-col w-full">
            <label htmlFor="lastName">Patient last name</label>
            <input
              id="lname"
              {...register('lname', {
                required: 'Patient last name is required',
              })}
              value={searchInput.lname}
              onChange={e => {
                setSearchInput({
                  ...searchInput,
                  lname: e.target.value,
                });
              }}
              className="rounded-md text-gray-500"
            />
            {errors.lname && <p>{errors?.lname?.message}</p>}
          </div>
        </div>
        <Button
          type="submit"
          className="mt-4"
          disabled={loading}
          loading={loading}
          text={loading ? 'Searching...' : 'Search'}
        />
      </form>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <div>
        <h2 className="font-bold text-lg">Search Results</h2>

        {(resultsArray.length ?? 0) > 0 ? (
          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 xl:grid-cols-3">
            {resultsArray.map((patient, _index) => (
              <SinglePatientCard
                patient={patient}
                buttonComponent={
                  <button
                    onClick={() => {
                      setSelectedPatients(prev => ({
                        ...prev,
                        [patient.ID]: patient,
                      }));
                      setSearchResponsePatients(prev => {
                        const updated = { ...prev };
                        delete updated[patient.ID];

                        return updated;
                      });
                    }}
                    className="bg-blue-500 text-white px-4 py-2 rounded"
                  >
                    Select
                  </button>
                }
              />
            ))}
          </div>
        ) : (
          <p>No results found.</p>
        )}
      </div>
      {/* <pre>{ChiroUpJSON.pretty(clinic)}</pre> */}

      {rows.length > 0 ? (
        <>
          <div className="pt-4">
            <hr />
            <h2 className="font-bold text-lg mt-4">Selected Patients</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((row: Patient) => (
              <SinglePatientCard
                patient={row}
                buttonComponent={
                  <button
                    onClick={() => {
                      setSearchResponsePatients(prev => {
                        const updated = { ...prev };
                        updated[row.ID] = row;

                        return updated;
                      });
                      setSelectedPatients(prev => {
                        const updated = { ...prev };
                        delete updated[row.ID];

                        return updated;
                      });
                    }}
                    className="bg-red-500 text-white px-4 py-2 rounded"
                  >
                    Deselect
                  </button>
                }
              />
            ))}
          </div>
        </>
      ) : null}
      {rows.length > 0 && (
        <div className="pt-4">
          <hr />
          <h2 className="font-bold text-lg mt-4">Operations</h2>
          <div className="flex space-x-4 mt-4">
            <Button
              onClick={previewBilling}
              className="bg-blue-500 text-white px-4 py-2 rounded"
              text="Preview Record Purge"
              loading={isFetching}
              disabled={isFetching}
            />
          </div>
        </div>
      )}

      {previewRows.length > 0 && (
        <div className="pt-4">
          <hr />
          <h2 className="font-bold text-lg mt-4">Patient Record Purge Preview</h2>
          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 xl:grid-cols-3">
            {previewRows.map((row, index) => (
              <div
                key={index}
                className="border border-gray-300 rounded-lg p-4 shadow-sm bg-gray-50"
              >
                <div className="flex flex-row justify-between">
                  <h3 className="font-bold text-md">
                    {row?.patient?.lname}, {row?.patient?.fname}
                  </h3>
                  <p className="text-sm text-gray-500">DOB: {row?.patient?.dateOfBirth}</p>
                </div>
                {row.tables && row.tables.length > 0 && (
                  <table className="mt-2 w-full text-sm text-left border-collapse border border-gray-300 bg-white">
                    <thead>
                      <tr>
                        <th className="border border-gray-300 px-2 py-1">Table</th>
                        <th className="border border-gray-300 px-2 py-1 text-right">Hits</th>
                        <th className="border border-gray-300 py-1 text-right pl-4">
                          <div
                            className="flex flex-row"
                            onClick={() => {
                              setPurgePatientTableDataList(prev => {
                                const updated = { ...prev };

                                for (const o of row.tables) {
                                  updated[`${o.name}.${row?.patient?.ID}`] = true;
                                }

                                return updated;
                              });
                            }}
                            onDoubleClick={() => {
                              setPurgePatientTableDataList(prev => {
                                const updated = { ...prev };

                                for (const o of row.tables) {
                                  updated[`${o.name}.${row?.patient?.ID}`] = false;
                                }

                                return updated;
                              });
                            }}
                          >
                            <CheckIcon className="w-6 h-6 text-gray-500 dark:text-dark-gray-400" />
                            <TrivialTooltip
                              text={`Single-click to select all, double-click to deselect all.`}
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.tables.map((table, tableIndex) => (
                        <tr key={tableIndex}>
                          <td className="border border-gray-300 px-2 py-1">{table.name}</td>
                          <td className="border border-gray-300 px-2 py-1 text-right">
                            {table.hits}
                          </td>
                          <td className="border border-gray-300 pl-4 py-1 text-right">
                            <div
                              onClick={() => {
                                setPurgePatientTableDataList(prev => ({
                                  ...prev,
                                  [`${table.name}.${row?.patient?.ID}`]:
                                    !purgePatientTableDataList[`${table.name}.${row?.patient?.ID}`],
                                }));
                              }}
                            >
                              {purgePatientTableDataList[`${table.name}.${row?.patient?.ID}`] ? (
                                <CheckCircleIcon className="w-6 h-6 text-green-500" />
                              ) : (
                                <CheckCircleIcon className="w-6 h-6 text-gray-500 dark:text-dark-gray-400" />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {Object.values(purgePatientTableDataList)?.some(v => v) && (
        <div className="pt-4">
          <hr />
          {/* <h2 className="font-bold text-lg mt-4">Purge Selected Records</h2> */}
          <div className="flex space-x-4 mt-4">
            <Button
              onClick={purgeRecordsPreview}
              className="bg-red-500 text-white px-4 py-2 rounded"
              text="Purge Selected Records"
              loading={isFetching}
              disabled={isFetching}
            />
          </div>
        </div>
      )}
      {actionRows.length > 0 && (
        <div className="pt-4">
          <hr />
          <h2 className="font-bold text-lg mt-4">Patient Record Purge Actions</h2>
          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2 xl:grid-cols-3">
            {actionRows.map((row, index) => (
              <div
                key={index}
                className="border border-gray-300 rounded-lg p-4 shadow-sm bg-gray-50"
              >
                <div className="flex flex-row justify-between">
                  <h3 className="font-bold text-md">
                    {row?.patient?.lname}, {row?.patient?.fname}
                  </h3>
                  <p className="text-sm text-gray-500">DOB: {row?.patient?.dateOfBirth}</p>
                </div>
                {row.tables && row.tables.length > 0 && (
                  <table className="mt-2 w-full text-sm text-left border-collapse border border-gray-300 bg-white">
                    <thead>
                      <tr>
                        <th className="border border-gray-300 px-2 py-1">Table</th>
                        <th className="border border-gray-300 px-2 py-1 text-right">Hits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.tables.map((table, tableIndex) => (
                        <tr key={tableIndex}>
                          <td className="border border-gray-300 px-2 py-1">{table.name}</td>
                          <td className="border border-gray-300 px-2 py-1 text-right">
                            {table.hits}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {actionRows && actionRows.length !== 0 && !purgeResults && (
        <div className="pt-4">
          <hr />
          <h2 className="font-bold text-lg mt-4">Confirm Purge Actions</h2>
          <AlertBlock
            message={[
              'Continuing by confirming below cannot be reversed.',
              'Once submitted, the data will be gone as if it never existed.',
              'This is a permanent action.',
              'Please confirm that you have read and understood the above.',
            ].join(' ')}
          />

          <div className="flex space-x-4 mt-4">
            <Button
              onClick={cancelPurgeActions}
              text="Cancel"
              disabled={isFetching || isPurging}
              color={ButtonColors.plainWithBorder}
            />
            {confirmPurgeAction && confirmPurgeAction === 'confirm' ? (
              <Button
                onClick={finallyPurgeActions}
                color={ButtonColors.primary}
                text="Confirmed"
                disabled={isFetching}
                loading={isFetching || isPurging}
              />
            ) : (
              <Button
                onClick={confirmPurgeActions}
                color={ButtonColors.primary}
                text="Confirm Record Purge"
                disabled={isFetching}
              />
            )}
          </div>
          <div className="mt-4">
            {confirmedPurgeActions && !isPurging ? (
              <Input
                placeholder="...confirm..."
                name="confirmPurgeAction"
                value={confirmPurgeAction}
                label={`Type the word confirm in the field below.`}
                onChange={e => {
                  setConfirmPurgeAction(_ => e);
                }}
              />
            ) : null}
          </div>
        </div>
      )}

      {purgeResults && (
        <div className="pt-4">
          <hr />
          <h2 className="font-bold text-lg mt-4">Patient Record Purge Results</h2>

          {purgeResults.okays && purgeResults.okays.length > 0 && (
            <div className="mt-4">
              <h3 className="font-semibold text-green-600">Successful Operations</h3>
              <table className="mt-2 w-full text-sm text-left border-collapse border border-gray-300 bg-white">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-4 py-2">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {purgeResults.okays.map((item, index) => (
                    <tr key={`success-${index}`}>
                      <td className="border border-gray-300 px-4 py-2">{item.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {purgeResults.errors && purgeResults.errors.length > 0 && (
            <div className="mt-4">
              <h3 className="font-semibold text-red-600">Failed Operations</h3>
              <table className="mt-2 w-full text-sm text-left border-collapse border border-gray-300 bg-white">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-4 py-2">Message</th>
                    <th className="border border-gray-300 px-4 py-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {purgeResults.errors.map((item, index) => (
                    <tr key={`error-${index}`}>
                      <td className="border border-gray-300 px-4 py-2">{item.message}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.eMessage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {purgeResults.remainingRecords && purgeResults.remainingRecords.length > 0 && (
            <div className="mt-4">
              <h3 className="font-semibold text-amber-600">Remaining Records</h3>
              <p className="text-sm text-gray-600 mb-2">
                The following records still exist after the purge operation:
              </p>
              <div className="grid grid-cols-1 gap-4 mt-2 sm:grid-cols-2 xl:grid-cols-3">
                {purgeResults.remainingRecords.map((row, index) => (
                  <div
                    key={`remaining-${index}`}
                    className="border border-gray-300 rounded-lg p-4 shadow-sm bg-gray-50"
                  >
                    <div className="flex flex-row justify-between">
                      <h3 className="font-bold text-md">
                        {row?.patient?.lname}, {row?.patient?.fname}
                      </h3>
                      <p className="text-sm text-gray-500">DOB: {row?.patient?.dateOfBirth}</p>
                    </div>
                    {row.tables && row.tables.length > 0 && (
                      <table className="mt-2 w-full text-sm text-left border-collapse border border-gray-300 bg-white">
                        <thead>
                          <tr>
                            <th className="border border-gray-300 px-2 py-1">Table</th>
                            <th className="border border-gray-300 px-2 py-1 text-right">Hits</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.tables.map((table, tableIndex) => (
                            <tr key={tableIndex}>
                              <td className="border border-gray-300 px-2 py-1">{table.name}</td>
                              <td className="border border-gray-300 px-2 py-1 text-right">
                                {table.hits}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-4 mt-4">
            <Button
              onClick={() => {
                setPurgeResults(null);
                cancelPurgeActions();
              }}
              text="Done"
              color={ButtonColors.primary}
            />
          </div>
        </div>
      )}
    </div>
  );
};

type SinglePatientCardProps = {
  patient: Patient;
  buttonComponent: React.ReactNode;
};

const SinglePatientCard: React.FC<SinglePatientCardProps> = ({ patient, buttonComponent }) => {
  return (
    <div className="relative list-none flex">
      <div className="flex items-center px-6 py-5 space-x-3 border border-gray-300 rounded-lg shadow-sm dark:border-dark-gray-800 bg-gray-50 dark:bg-dark-gray-700 hover:border-gray-400 dark:hover:border-dark-gray-500 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 w-full">
        <div className="shrink-0">
          {patient?.profilePhoto ? (
            <UploadedImage
              url={patient.profilePhoto}
              // className="h-14 w-14 rounded-full"
            />
          ) : patient.$avatarBase64 ? (
            <img
              className="rounded-full h-14 w-14"
              src={`data:image/jpg;base64, ${patient.$avatarBase64}`}
              alt="avatar"
            />
          ) : (
            <svg
              className="shrink-0 text-gray-300 h-14 w-14 dark:text-dark-gray-200 group-hover:text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="2 2 16 16"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-dark-gray-200 flex items-center">
            {`${patient?.lname?.trim()}, ${patient?.fname?.trim()} ${
              patient?.pname ? `(${patient?.pname?.trim()})` : ''
            } `}
          </div>
          <span className="text-xs font-light text-gray-500 truncate dark:text-dark-gray-400">
            {generatePatientSubtitle(patient.age, patient.gender, false, patient.dateOfBirth)}
          </span>
          <p className="mt-1 text-xs font-light leading-4 text-gray-500 truncate dark:text-dark-gray-400">
            {patient.email}
          </p>
          <p className="text-xs font-light leading-4 text-gray-500 truncate dark:text-dark-gray-400 ">
            {formatPhone(patient.phone)}
          </p>
        </div>
        {buttonComponent}
      </div>
    </div>
  );
};

export default Patients;
